export const ADMIN_OVERVIEW_SCHEMA_VERSION = 4 as const;
export const ADMIN_OVERVIEW_PERIODS = [7, 30, 90] as const;
export const ADMIN_OVERVIEW_PRESETS = [
  "today",
  "yesterday",
  "24h",
  "7d",
  "30d",
  "90d",
] as const;
export const ADMIN_OVERVIEW_TIME_ZONE = "Asia/Seoul" as const;
export const ADMIN_OVERVIEW_MAX_DAYS = 400;
export const ADMIN_DETAIL_LIMITS = {
  analytics: 100,
  feedback: 50,
  installations: 100,
  updateChecks: 100,
  webActivity: 100,
  webInteractions: 100,
} as const;
export const ADMIN_FEEDBACK_DETAIL_BUDGET_BYTES = 512 * 1_024;

export const ADMIN_TRAFFIC_SOURCES = [
  "direct",
  "threads",
  "instagram",
  "search",
  "social",
  "internal",
  "other",
  "unknown",
] as const;

export type AdminTrafficSource =
  (typeof ADMIN_TRAFFIC_SOURCES)[number];

export interface LandingVisitAttribution {
  readonly campaign: string | null;
  readonly entryPath: string;
  readonly referrerHost: string | null;
  readonly schemaVersion: 1;
  readonly source: AdminTrafficSource;
  readonly utmMedium: string | null;
  readonly utmSource: string | null;
}

export const WEB_INTERACTION_EVENTS = [
  "farm_visit",
  "download_intent",
] as const;

export type WebInteractionEventName =
  (typeof WEB_INTERACTION_EVENTS)[number];

export interface WebInteractionAttribution extends LandingVisitAttribution {
  readonly dimension: string;
  readonly eventName: WebInteractionEventName;
}

export type AdminOverviewPeriod =
  (typeof ADMIN_OVERVIEW_PERIODS)[number];

export type AdminOverviewPreset =
  (typeof ADMIN_OVERVIEW_PRESETS)[number];

export type AdminOverviewRequest =
  | {
      readonly kind: "preset";
      readonly preset: AdminOverviewPreset;
    }
  | {
      readonly kind: "custom";
      readonly since: string;
      readonly until: string;
    };

export interface AdminOverviewWindow {
  readonly kind: "custom" | "legacy_days" | "preset";
  readonly preset: AdminOverviewPreset | null;
  readonly since: string;
  readonly timeZone: typeof ADMIN_OVERVIEW_TIME_ZONE;
  readonly until: string;
}

export interface AdminDailyActivity {
  readonly activeSeconds: number;
  readonly appSessions: number;
  readonly date: string;
  readonly downloadIntents: number;
  readonly downloadRequests: number;
  readonly landingVisits: number;
  readonly updateChecks: number;
  readonly webFarmVisits: number;
}

export interface AdminVersionCount {
  readonly count: number;
  readonly version: string;
}

export interface AdminFeedbackCount {
  readonly count: number;
  readonly status: "new" | "reviewing" | "resolved";
}

export interface AdminTrafficSourceCount {
  readonly count: number;
  readonly source: AdminTrafficSource;
}

export interface AdminWebActivityEvent {
  readonly browser: "chrome" | "edge" | "firefox" | "other" | "safari";
  readonly campaign: string | null;
  readonly country: string | null;
  readonly device: "bot" | "desktop" | "mobile" | "other" | "tablet";
  readonly dimension: string;
  readonly entryPath: string | null;
  readonly language: string | null;
  readonly metric: "landing_visit" | "mac_download";
  readonly occurredAt: string;
  readonly os: "android" | "ios" | "linux" | "macos" | "other" | "windows";
  readonly referrerHost: string | null;
  readonly source: AdminTrafficSource;
  readonly utmMedium: string | null;
  readonly utmSource: string | null;
}

export interface AdminWebInteractionEvent {
  readonly browser: AdminWebActivityEvent["browser"];
  readonly campaign: string | null;
  readonly country: string | null;
  readonly device: Exclude<AdminWebActivityEvent["device"], "bot">;
  readonly dimension: string;
  readonly entryPath: string;
  readonly eventName: WebInteractionEventName;
  readonly language: string | null;
  readonly occurredAt: string;
  readonly os: AdminWebActivityEvent["os"];
  readonly referrerHost: string | null;
  readonly source: AdminTrafficSource;
  readonly utmMedium: string | null;
  readonly utmSource: string | null;
}

export interface AdminInstallationDetail {
  readonly alias: string;
  readonly channel: "preview" | "stable";
  readonly currentVersion: string;
  readonly firstSeenAt: string;
  readonly lastSeenAt: string;
  readonly platform: "macos";
  readonly totalCheckCount: number;
}

export interface AdminUpdateCheckDetail {
  readonly appVersion: string;
  readonly checkCount: number;
  readonly firstCheckedAt: string;
  readonly installationAlias: string;
  readonly lastCheckedAt: string;
  readonly observedDate: string;
}

export interface AdminAnalyticsEventDetail {
  readonly appVersion: string;
  readonly durationSeconds: number | null;
  readonly eventName:
    | "app_open"
    | "feedback_open"
    | "feedback_submit"
    | "screen_view"
    | "session_summary"
    | "update_check";
  readonly occurredAt: string;
  readonly outcome: "available" | "current" | "failed" | "sent" | null;
  readonly route:
    | "calendar"
    | "farm"
    | "observation"
    | "portfolio"
    | "portfolio_new"
    | "settings"
    | "slimes"
    | null;
  readonly sessionAlias: string;
  readonly surface: "desktop" | "menubar";
}

export interface AdminFeedbackDetail {
  readonly appVersion: string | null;
  readonly createdAt: string;
  readonly diagnosticsConsent: boolean;
  readonly email: string | null;
  readonly feedbackId: string;
  readonly message: string;
  readonly osVersion: string | null;
  readonly replyRequested: boolean;
  readonly status: AdminFeedbackCount["status"];
}

export interface AdminOperationsOverview {
  readonly daily: readonly AdminDailyActivity[];
  readonly days: number;
  readonly downloadIntentsByVersion: readonly AdminVersionCount[];
  readonly downloadRequestsByVersion: readonly AdminVersionCount[];
  readonly feedbackByStatus: readonly AdminFeedbackCount[];
  readonly generatedAt: string;
  readonly installationsByVersion: readonly AdminVersionCount[];
  readonly recentAnalyticsEvents: readonly AdminAnalyticsEventDetail[];
  readonly recentFeedback: readonly AdminFeedbackDetail[];
  readonly recentInstallations: readonly AdminInstallationDetail[];
  readonly recentUpdateChecks: readonly AdminUpdateCheckDetail[];
  readonly recentWebActivity: readonly AdminWebActivityEvent[];
  readonly recentWebInteractions: readonly AdminWebInteractionEvent[];
  readonly schemaVersion: typeof ADMIN_OVERVIEW_SCHEMA_VERSION;
  readonly summary: {
    readonly activeInstallations: number;
    readonly activeSeconds: number;
    readonly appSessions: number;
    readonly downloadIntents: number;
    readonly downloadRequests: number;
    readonly landingVisits: number;
    readonly pendingFeedback: number;
    readonly updateChecks: number;
    readonly webFarmVisits: number;
  };
  readonly trafficSources: readonly AdminTrafficSourceCount[];
  readonly window: AdminOverviewWindow;
}

export function isAdminOverviewPeriod(
  value: number,
): value is AdminOverviewPeriod {
  return ADMIN_OVERVIEW_PERIODS.some((period) => period === value);
}

export function isAdminOverviewPreset(
  value: string,
): value is AdminOverviewPreset {
  return ADMIN_OVERVIEW_PRESETS.some((preset) => preset === value);
}
